/** Catalog units and runtime weapon feel, shared by world play and race play. */
import type { WeaponDef } from './rpg.js';
const clamp = (v:number,lo:number,hi:number)=>Math.max(lo,Math.min(hi,v));
export function weaponStats(w: Partial<WeaponDef>) {
  return {
    damage:w.damage??10, rpm:w.rpm??300, speed:w.projectileSpeed??420, range:w.range??(w.projectileSpeed??420)*1.4,
    spread:w.spread??0, hipSpread:w.hipSpread??.012, adsZoom:w.adsZoom??1.3, energyCost:w.energyCost??3,
    damageFalloff:clamp(w.damageFalloff??0,0,1), falloffStart:Math.max(0,w.falloffStart??0),
    gravityScale:Math.max(0,w.gravityScale??0), pierce:Math.max(0,Math.floor(w.pierce??0)),
    explosiveDamage:Math.max(0,w.explosiveDamage??0), explosiveRadius:Math.max(0,w.explosiveRadius??0), explosiveFalloff:clamp(w.explosiveFalloff??1,0,1),
    mobility:clamp(w.mobility??1,0,1), bloomPerShot:w.bloomPerShot??.3, bloomMax:w.bloomMax??1, bloomRecovery:w.bloomRecovery??3.5,
    recoil:w.recoil??.015, recoilRecovery:w.recoilRecovery??24, chargeTime:Math.max(0,w.chargeTime??0), chargeDecayTime:Math.max(.001,w.chargeDecayTime??.7),
    deployTime:w.deployTime??.3, stowTime:w.stowTime??.32, impact:w.impact??1,
  };
}
export type WeaponStats = ReturnType<typeof weaponStats>;
export function directDamage(w:WeaponStats,distance:number):number {
  const t=clamp((distance-w.falloffStart)/Math.max(.001,w.range-w.falloffStart),0,1);
  return w.damage*(1-t*w.damageFalloff);
}
export function blastDamage(w:WeaponStats,distance:number):number {
  if(w.explosiveRadius<=0||distance>w.explosiveRadius)return 0;
  return w.explosiveDamage*(1-clamp(distance/w.explosiveRadius,0,1)*w.explosiveFalloff);
}
export function weaponSpread(w:WeaponStats,bloom:number,movement=0,airborne=false,aim=1):number {
  return w.spread*(1+Math.max(0,bloom)*2.5)+.0045*clamp(movement,0,1)+(airborne?.009:0)+w.hipSpread*(1-clamp(aim,0,1));
}
/** At zero mobility flight is disabled by the controller; this finite multiplier
 * also keeps transitions and telemetry free of infinities. */
export function weaponFlightCost(mobility:number):number {return 1/Math.max(.05,clamp(mobility,0,1));}
/** Borrow whole pairs from ten physical modules. Four make the lightest form;
 * progressively heavier forms consume limb pairs, then the upper support pair.
 * The low-mobility tier uses all ten, including the Tempest at 0.18. */
export function weaponWingModuleCount(mobility:number):number {
  const value=Number.isFinite(mobility)?clamp(mobility,0,1):1;
  return 4+2*Math.ceil((1-value)*3);
}
/** Live trigger charge and cadence, shared by both games. Assembly is a visual
 * prerequisite owned by the avatar, never an additional firing timer here. */
export class WeaponCycle {
  bloom=0;
  /** Normalized meter: the reticle and rotor read this exact value. */
  charge=0;
  private cooldown=0;
  private requested=false;
  private held=false;
  reset():void {this.cooldown=this.charge=this.bloom=0;this.requested=this.held=false;}
  /** A short uncharged click may be sampled after mouseup; it is valid only in
   * this update. It is never saved through cooldown or assembly. Charge itself
   * can only grow while the trigger is physically held and aiming is allowed. */
  update(dt:number,w:WeaponStats,trigger:boolean,aim:boolean,pressed=false):void {
    dt=Number.isFinite(dt)?Math.max(0,dt):0;
    this.cooldown=Math.max(0,this.cooldown-dt);
    this.bloom=Math.max(0,this.bloom-w.bloomRecovery*dt);
    this.held=trigger&&aim;
    this.requested=aim&&(trigger||pressed&&w.chargeTime===0);
    this.charge=w.chargeTime>0?clamp(this.charge+(this.held?dt/w.chargeTime:-dt/w.chargeDecayTime),0,1):0;
  }
  ready(w:WeaponStats):boolean {return this.requested&&this.cooldown<=1e-9&&(w.chargeTime===0||this.charge>=1-1e-9);}
  commit(w:WeaponStats):void {
    this.cooldown=60/Math.max(1,w.rpm);this.requested=this.held;
    this.bloom=Math.min(w.bloomMax,this.bloom+w.bloomPerShot);
  }
}

/** Compact inspection text shared by both weapon selectors. */
export function weaponSummary(weapon:Partial<WeaponDef>):string {
  const w=weaponStats(weapon);
  const wings=` · ${weaponWingModuleCount(w.mobility)}/10 wing pieces`;
  if(weapon.melee)return `${weapon.melee.damage} damage · ${weapon.melee.reach} m reach${wings}`;
  return `${w.damage} damage · ${w.rpm} RPM · ${w.range} m · ${w.energyCost} energy/shot · ${w.adsZoom}× zoom`
    + (w.chargeTime?` · hold fire to charge ${w.chargeTime.toFixed(2)} s`:'')
    + (w.pierce?` · pierces ${w.pierce}`:'')
    + (w.explosiveDamage?` · blast ${w.explosiveDamage} / ${w.explosiveRadius} m`:'')
    + (w.gravityScale?` · arc ${w.gravityScale}`:'')
    + wings
    + (w.mobility===0?' · no flight while deployed':w.mobility<1?` · hover cost ×${weaponFlightCost(w.mobility).toFixed(1)}`:' · full flight mobility');
}
